import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Button,
  CardHeader,
} from "reactstrap";
import classnames from "classnames";
import BreadCrumb from "Components/Common/BreadCrumb";
import TemplatePreviewModal from "Components/Quotes/ManageQuotes/TemplatePreviewModal";
import TemplateFirst from "Components/Templates/TemplateFirst";
import TemplateSecond from "Components/Templates/TemplateSecond";
import TemplateThird from "Components/Templates/TemplateThird";
import ItemsPane from "Components/QuoteAndInvoice/ItemsPane";
import SummaryPane from "Components/QuoteAndInvoice/SummaryPane";
import SectionsPane from "Components/QuoteAndInvoice/SectionsPane";
import CustomButton from "Components/Custom/CustomButton";
// import Icon from "Components/Icons/Icon";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import {
  // editQuote,
  getCategories,
  getPresetQuoteDetail,
  getQuoteById,
} from "slices/thunks";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { clearQuoteDetails, selectQuotesState } from "slices/quotes/reducer";
import Loader from "Components/Common/Loader";
import { PresetQuotePayload } from "types/preset.api";
import { addItemsToState } from "slices/items/reducer";
import Select from "react-select";
import { addDaysToToday } from "utils/formatDates";
import { ClientsPayloadData } from "types/client.api";
import { ErrorHandler, SuccessToast } from "utils/toast";
import ShareModal from "Components/Modals/ShareModal";
import OverlayGuide from "Components/Common/TourGuide/OverlayGuide";
import { useScreenTour } from "tour/useScreenTour";
import { tourElementsIds } from "tour/tourElementsIds";
import { editQuoteApi } from "helpers/ApiManager";
import FullScreenLoader from "Components/Common/FullScreenLoader";
import { SectionPayloadData } from "types/section.api";

const ManageQuotes = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const quoteSelector = createSelector(selectQuotesState, (state) => ({
    quoteDetails: state.quoteDetails,
    isLoading: state.status.fetchById,
    error: state.errors.fetchById,
  }));
  const { user } = useAppSelector((state) => state?.auth);
  const addedItems = useAppSelector((state) => state?.item?.addItems);
  const [selectedDate, setSelectedDate] = useState<{
    issueDate: string;
    dueDate: string;
  }>({ issueDate: "", dueDate: "" });
  const [selectedClientDetail, setSelectedClientDetail] =
    useState<ClientsPayloadData | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);

  const { quoteDetails, isLoading, error } = useSelector(quoteSelector);
  const [presetQuote, setPresetQuote] = useState<PresetQuotePayload | null>(
    null,
  );
  const [customActiveTab, setcustomActiveTab] = useState<string>("1");
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [selectedSections, setSelectedSections] = useState<
    SectionPayloadData[]
  >([]);
  const { sections } = useAppSelector((state) => state.section);
  // const [pdfUrl, setPdfUrl] = useState<string>(quoteDetails?.url ?? "");

  // Tab completion state
  const [isSummaryCompleted, setIsSummaryCompleted] = useState<boolean>(false);
  const [isItemsCompleted, setIsItemsCompleted] = useState<boolean>(false);
  const [jobDescription, setJobDescription] = useState<string>("");
  const [quoteTitle, setQuoteTitle] = useState<string>("");

  const [showTemplatePreview, setShowTemplatePreview] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>(
    quoteDetails?.template ?? "classic",
  );
  const [templateLoading, setTemplateLoading] = useState(false);
  const [selectedTemplateLoading, setSelectedTemplateLoading] =
    useState<boolean>(false);
  const [isHidePhoneNumber, setIsHidePhoneNumber] = useState<boolean>(false);

  useEffect(() => {
    if (isEdit && quoteDetails) {
      setIsHidePhoneNumber(!quoteDetails?.is_company_phone_number_show);
    }
  }, [isEdit, quoteDetails]);

  useEffect(() => {
    setSelectedTemplate(quoteDetails?.template ?? "classic");
    setJobDescription(quoteDetails?.job_description || "");
    setQuoteTitle(quoteDetails?.title || "");
    // setPdfUrl(quoteDetails?.url ?? "");
  }, [quoteDetails]);

  useEffect(() => {
    if (sections?.length) {
      const added = sections.filter((s) => s.is_added);
      setSelectedSections(added);
    }
  }, [sections]);

  const configData = useAppSelector((state) => state.config.configData);
  // const [isLoadingDownload, setIsLoadingDownload] = useState<boolean>(false);

  const summaryTour = useScreenTour("quoteSummary", {
    condition: customActiveTab === "1" && !isEdit,
  });

  const itemsTour = useScreenTour("quoteItems", {
    condition: customActiveTab === "2" && !isEdit,
  });

  const sectionTour = useScreenTour("quoteSections", {
    condition: customActiveTab === "3" && !isEdit,
  });

  useEffect(() => {
    if (quoteDetails && !state?.template) {
      console.log("quoteDetails.items", quoteDetails.items.length);
      dispatch(addItemsToState(quoteDetails.items));
    }
  }, [dispatch, quoteDetails]);

  useEffect(() => {
    if (state?.id) {
      // if (state?.isDuplicate) {
      //   dispatch(
      //     getDuplicateQuoteById({
      //       quote_id: state.id,
      //       isDuplicate: state?.isDuplicate || false ? 1 : 0,
      //     })
      //   );
      // } else {
      // }
      dispatch(getQuoteById(state.id));

      setIsEdit(true);
    }
  }, [dispatch, state]);

  useEffect(() => {
    if (state?.template) {
      console.log("Preset Quote Template", state.template);
      dispatch(getPresetQuoteDetail({ templateId: state.template.id }))
        .unwrap()
        .then((res) => {
          dispatch(addItemsToState(res.payload.items || []));
        });
      setPresetQuote(state.template);
    }
  }, [dispatch, state]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [customActiveTab]);

  const filterOptions = useMemo(
    () =>
      [
        configData?.document_setting?.categories
          ?.by_category_subcategory_item && {
          label: "All",
          value: "by-category-subcategory-item",
        },
        configData?.document_setting?.categories?.by_category && {
          label: "By Category",
          value: "by-category",
        },
        configData?.document_setting?.categories?.by_subcategory && {
          label: "By Subcategory",
          value: "by-subcategory",
        },
        configData?.document_setting?.categories?.by_item && {
          label: "By Items",
          value: "by-item",
        },
      ].filter((item): item is { label: string; value: string } =>
        Boolean(item),
      ),
    [
      configData?.document_setting?.categories?.by_category,
      configData?.document_setting?.categories?.by_category_subcategory_item,
      configData?.document_setting?.categories?.by_item,
      configData?.document_setting?.categories?.by_subcategory,
    ],
  );

  const [selectedFilter, setSelectedFilter] = useState<{
    label: string;
    value: string;
  } | null>(null);

  useEffect(() => {
    if (!filterOptions.length) return;

    const fromQuote = quoteDetails?.categorised
      ? filterOptions.find(
          (option) => option.value === quoteDetails.categorised,
        )
      : null;

    setSelectedFilter(fromQuote ?? filterOptions[3]);
  }, [filterOptions, quoteDetails?.categorised]);

  useEffect(() => {
    const formatUI = (dateVal: string | Date) => {
      const d = new Date(dateVal);
      const day = String(d.getDate()).padStart(2, "0");
      const month = d.toLocaleString("en-GB", { month: "short" }); // Apr, Jun, etc.
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    };

    if (isEdit && quoteDetails) {
      const issueDate = quoteDetails.quote_date
        ? formatUI(quoteDetails.quote_date)
        : formatUI(new Date()); // fallback today

      const dueDate = quoteDetails.expiry_date
        ? formatUI(quoteDetails.expiry_date)
        : formatUI(
            addDaysToToday(
              configData?.billing_preferences.quote_expiration || 0,
            ),
          );

      setSelectedDate({ issueDate, dueDate });
    } else {
      const today = new Date();
      const expiry = addDaysToToday(
        configData?.billing_preferences.quote_expiration || 0,
      );

      setSelectedDate({
        issueDate: formatUI(today),
        dueDate: formatUI(expiry),
      });
    }
  }, [isEdit, quoteDetails, configData?.billing_preferences?.quote_expiration]);

  const toggleCustom = (tab: any) => {
    // Check if tab is clickable based on completion status
    if (tab === "1") {
      // Summary tab is always accessible
      setcustomActiveTab(tab);
    } else if (tab === "2" && (isSummaryCompleted || isEdit)) {
      console.log("tab 2 clicked");
      // Items tab is accessible only if summary is completed
      setcustomActiveTab(tab);
    } else if (
      tab === "3" &&
      ((isSummaryCompleted && isItemsCompleted) || isEdit)
    ) {
      // Sections tab is accessible only if both summary and items are completed
      setcustomActiveTab(tab);
    }
  };

  // Handler for Summary submission
  const handleSummarySubmit = () => {
    console.log("Summary submitted");
    setIsSummaryCompleted(true);
    setcustomActiveTab("2"); // Navigate to Items tab
  };

  // Handler for Items submission
  const handleItemsSubmit = () => {
    setIsItemsCompleted(true);
    setcustomActiveTab("3"); // Navigate to Sections tab
  };

  const handleSectionSubmit = () => {
    // navigate("/quotes");
    navigate("/quote-details", { state: { id: quoteDetails?.id } });
  };

  //INITIALLY FETCH ALL CATEGORY AND SUB CATEGORY
  useEffect(() => {
    dispatch(getCategories());
  }, []);

  useEffect(() => {
    dispatch(clearQuoteDetails());
  }, [dispatch]);

  const openShareModal = () => setShowShareModal(true);
  const closeShareModal = () => setShowShareModal(false);

  // const handleShare = async () => {
  //   try {
  //     openShareModal();
  //   } catch (error) {
  //     ErrorHandler(error);
  //   }
  // };

  // const handleDownload = useCallback(async () => {
  //   try {
  //     setIsLoadingDownload(true);
  //     console.log("Downloading Quote PDF...", pdfUrl);

  //     const pdf = `${pdfUrl}?download=1&t=${Date.now()}`;

  //     const link = document.createElement("a");
  //     link.href = pdf;
  //     link.download = `${quoteDetails?.reference_number}.pdf`;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } catch (error) {
  //     ErrorHandler(error);
  //   } finally {
  //     setIsLoadingDownload(false);
  //   }
  // }, [pdfUrl, quoteDetails?.reference_number]);

  if (isLoading === "loading") {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Loader error={error} />
      </Container>
    );
  }

  // const handleToggleSection = (
  //   section: SectionPayloadData,
  //   isAdded: boolean,
  // ) => {
  //   setSelectedSections((prev) =>
  //     isAdded ? [...prev, section] : prev.filter((s) => s.id !== section.id),
  //   );
  // };

  return (
    <React.Fragment>
      <FullScreenLoader
        isLoading={selectedTemplateLoading}
        message="Loading template..."
      />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEdit ? "Edit Quote" : "New Quote"}
            pageTitle={"Quotes"}
          />
          <div className="d-flex align-items-center justify-content-between w-100 mb-4">
            {/* LEFT TITLE AREA */}
            <div className="flex-shrink-0">
              <p className="fs-3 fw-bold mb-1">
                {isEdit ? "Edit Quote" : "New Quote"}
              </p>
            </div>

            {/* RIGHT BUTTON
           {quoteDetails?.id && (
              <div className="gap-2  d-flex">
                <CustomButton
                  text="Download"
                  className="bg-primary br-7"
                  leftIcon={<Icon name="WhiteDownload" />}
                  onClick={handleDownload}
                  loading={isLoadingDownload}
                />
                <CustomButton
                  text="Share"
                  className="bg-blue br-7"
                  leftIcon={<Icon name="Share" />}
                  onClick={handleShare}
                />
              </div>
            )} */}
          </div>

          <Row>
            <Col
              lg={6}
              xl={7}
              xxl={8}
              id={tourElementsIds.summaryItemSectionTour}
            >
              <Card className="br-10">
                <CardBody>
                  <Row className="row align-items-end border-bottom ">
                    <Col className="">
                      <div id="mail-filter-navlist">
                        <Nav
                          className="nav nav-tabs nav-tabs-custom nav-primary gap-1 text-center border-bottom-0"
                          role="tablist"
                        >
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: customActiveTab === "1",
                              })}
                              onClick={() => {
                                toggleCustom("1");
                              }}
                              style={{
                                fontSize: 16,
                                fontWeight: customActiveTab === "1" ? 500 : 400,
                                fontFamily: "Inter Tight",
                                color:
                                  customActiveTab === "1"
                                    ? "#082B60"
                                    : "#2D2D2D",
                                cursor: "pointer",
                              }}
                            >
                              Summary
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: customActiveTab === "2",
                                disabled: !isEdit && !isSummaryCompleted,
                              })}
                              onClick={() => {
                                toggleCustom("2");
                              }}
                              style={{
                                fontSize: 16,
                                fontWeight: customActiveTab === "2" ? 500 : 400,
                                fontFamily: "Inter Tight",
                                color:
                                  !isSummaryCompleted && !isEdit
                                    ? "#B0B0B0"
                                    : customActiveTab === "2"
                                    ? "#082B60"
                                    : "#2D2D2D",
                                cursor:
                                  isSummaryCompleted || isEdit
                                    ? "pointer"
                                    : "not-allowed",
                                opacity: isSummaryCompleted || isEdit ? 1 : 0.6,
                              }}
                            >
                              Items
                            </NavLink>
                          </NavItem>
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: customActiveTab === "3",
                                disabled:
                                  !isEdit &&
                                  (!isSummaryCompleted || !isItemsCompleted),
                              })}
                              onClick={() => {
                                toggleCustom("3");
                              }}
                              style={{
                                fontSize: 16,
                                fontWeight: customActiveTab === "3" ? 500 : 400,
                                fontFamily: "Inter Tight",
                                color:
                                  (!isSummaryCompleted || !isItemsCompleted) &&
                                  !isEdit
                                    ? "#B0B0B0"
                                    : customActiveTab === "3"
                                    ? "#082B60"
                                    : "#2D2D2D",
                                cursor:
                                  (isSummaryCompleted && isItemsCompleted) ||
                                  isEdit
                                    ? "pointer"
                                    : "not-allowed",
                                opacity:
                                  (isSummaryCompleted && isItemsCompleted) ||
                                  isEdit
                                    ? 1
                                    : 0.6,
                              }}
                            >
                              Sections
                            </NavLink>
                          </NavItem>
                        </Nav>
                      </div>
                    </Col>
                  </Row>

                  <TabContent activeTab={customActiveTab}>
                    <TabPane tabId="1" id="home1">
                      <SummaryPane
                        clientPayload={state?.client_details_payload}
                        template={selectedTemplate}
                        categorised={selectedFilter?.value ?? ""}
                        isEdit={isSummaryCompleted || isEdit}
                        onSubmit={handleSummarySubmit}
                        quoteData={
                          isSummaryCompleted || isEdit ? quoteDetails : null
                        }
                        presetQuote={presetQuote}
                        onDatesChange={(
                          quoteDate?: string,
                          expiryDate?: string,
                        ) => {
                          setSelectedDate((prev) => ({
                            issueDate: quoteDate ?? prev.issueDate,
                            dueDate: expiryDate ?? prev.dueDate,
                          }));
                        }}
                        onClientOptionChange={(client?: ClientsPayloadData) => {
                          setSelectedClientDetail(client ?? null);
                        }}
                        onJobDescriptionChange={(description) => {
                          setJobDescription(description);
                        }}
                        onQuoteTitleChange={(title) => {
                          setQuoteTitle(title);
                        }}
                        isHidePhoneNumber={isHidePhoneNumber}
                        handleHidePhoneNumberToggle={() =>
                          setIsHidePhoneNumber((prev) => !prev)
                        }
                      />
                    </TabPane>
                    <TabPane tabId="2">
                      <ItemsPane
                        onSubmit={handleItemsSubmit}
                        presetQuote={presetQuote}
                        isEdit={isEdit}
                      />
                    </TabPane>
                    <TabPane tabId="3">
                      <SectionsPane
                        quoteDetails={quoteDetails}
                        onSubmit={handleSectionSubmit}
                        isEdit={isEdit}
                      />
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
            <Col
              lg={6}
              xl={5}
              xxl={4}
              id={tourElementsIds.quoteLivePreviewTour}
            >
              <Card className="br-10">
                <CardHeader className="br-tl-10 br-tr-10 border-bottom-0">
                  <div className="d-flex w-100 justify-content-between align-items-center p-1">
                    <Button className="btn bg-green-50 border-0 text-green d-flex gap-2 justify-content-center align-items-center px-2">
                      <span
                        className="d-inline-block bg-success rounded-circle mx-2 shadow-lg shadow-live"
                        style={{
                          width: "10px",
                          height: "10px",
                        }}
                      ></span>
                      Live Preview
                    </Button>
                    <div className="d-flex gap-2">
                      {customActiveTab === "2" && addedItems.length > 0 && (
                        <div>
                          <Select
                            name="option"
                            options={filterOptions}
                            value={selectedFilter}
                            onChange={async (option: any) => {
                              if (quoteDetails?.id) {
                                console.log("option", option);
                                try {
                                  setSelectedTemplateLoading(true);
                                  const response = await editQuoteApi({
                                    quote_id: quoteDetails?.id,
                                    categorised: option.value,
                                  });
                                  console.log(response);
                                  // setPdfUrl(response.payload.url);
                                  SuccessToast(response.message);
                                  setSelectedFilter(option);
                                } catch (err: any) {
                                  ErrorHandler(err);
                                } finally {
                                  setSelectedTemplateLoading(false);
                                }
                              }
                            }}
                            classNamePrefix="react-select"
                            className="auth-form-dropdown-control"
                            placeholder="Filter By"
                            isSearchable={false}
                            isClearable={false}
                            loadingMessage={() => "Searching..."}
                            noOptionsMessage={() => "No options found"}
                            menuPortalTarget={document.body}
                            styles={{
                              container: (base: any) => ({
                                ...base,
                                width: 145,
                              }),
                              control: (base: any) => ({
                                ...base,
                                width: "100%",
                                minWidth: 141,
                              }),
                              menuPortal: (base: any) => ({
                                ...base,
                                zIndex: 9999,
                              }),
                            }}
                          />
                        </div>
                      )}
                      <CustomButton
                        newClassName="bg-white border-1 border-primary d-flex align-items-center justify-content-center"
                        onClick={() => {
                          setShowTemplatePreview(true);
                        }}
                        text="Templates"
                        textColor="text-primary"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="gap-4 d-flex flex-column">
                  {selectedTemplate === "classic" && (
                    <div className="template-preview-wrapper">
                      <TemplateFirst
                        companyDetails={user?.company}
                        clientDetails={selectedClientDetail}
                        selectedOption={selectedFilter?.value}
                        tableData={addedItems}
                        referenceNumber={
                          quoteDetails?.reference_number ?? "QT-2025-01"
                        }
                        issueDate={selectedDate?.issueDate}
                        dueDate={selectedDate?.dueDate}
                        selectedTab={customActiveTab}
                        jobDescription={jobDescription}
                        allSelectedSections={selectedSections}
                        quoteTitle={quoteTitle}
                        isCompanyPhoneHidden={isHidePhoneNumber}
                        isCompanyNameHidden={
                          !user?.company?.is_company_name_show
                        }
                      />
                    </div>
                  )}
                  {selectedTemplate === "modern" && (
                    <div className="template-preview-wrapper">
                      <TemplateSecond
                        companyDetails={user?.company}
                        clientDetails={selectedClientDetail}
                        selectedOption={selectedFilter?.value}
                        tableData={addedItems}
                        referenceNumber={
                          quoteDetails?.reference_number ?? "QT-2025-01"
                        }
                        issueDate={selectedDate?.issueDate}
                        dueDate={selectedDate?.dueDate}
                        selectedTab={customActiveTab}
                        jobDescription={jobDescription}
                        allSelectedSections={selectedSections}
                        quoteTitle={quoteTitle}
                        isCompanyPhoneHidden={isHidePhoneNumber}
                        isCompanyNameHidden={
                          !user?.company?.is_company_name_show
                        }
                      />
                    </div>
                  )}
                  {selectedTemplate === "elegant" && (
                    <div className="template-preview-wrapper">
                      <TemplateThird
                        companyDetails={user?.company}
                        clientDetails={selectedClientDetail}
                        selectedOption={selectedFilter?.value}
                        tableData={addedItems}
                        referenceNumber={
                          quoteDetails?.reference_number ?? "QT-2025-01"
                        }
                        issueDate={selectedDate?.issueDate}
                        dueDate={selectedDate?.dueDate}
                        selectedTab={customActiveTab}
                        jobDescription={jobDescription}
                        allSelectedSections={selectedSections}
                        quoteTitle={quoteTitle}
                        isCompanyPhoneHidden={isHidePhoneNumber}
                        isCompanyNameHidden={
                          !user?.company?.is_company_name_show
                        }
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <TemplatePreviewModal
          template={selectedTemplate}
          showModal={showTemplatePreview}
          toggle={() => {
            setShowTemplatePreview(false);
          }}
          loading={templateLoading}
          onContinue={async (template: string) => {
            if (quoteDetails?.id) {
              try {
                setTemplateLoading(true);
                const response = await editQuoteApi({
                  quote_id: quoteDetails?.id,
                  template: template,
                });
                console.log(response);
                SuccessToast(response.message);
                setSelectedTemplate(template);
                setShowTemplatePreview(false);

                return response;
              } catch (err: any) {
                ErrorHandler(err);
                setTemplateLoading(false);
              } finally {
                setTemplateLoading(false);
              }
            }
            setSelectedTemplate(template);
            setShowTemplatePreview(false);
          }}
        />
        <ShareModal
          isOpen={showShareModal}
          onClose={closeShareModal}
          pdfUrl={quoteDetails?.url}
          route_url={quoteDetails?.route_url}
          isInvoice={false}
          quoteDetails={quoteDetails}
          isShareFromQuote
        />
      </div>

      <OverlayGuide {...summaryTour} />
      <OverlayGuide {...itemsTour} />
      <OverlayGuide {...sectionTour} />
    </React.Fragment>
  );
};

export default ManageQuotes;
